/**
 * GrowthDashboard — Phase 1 of the Growth Engine (Marketing module).
 *
 * Single page that gives the founder an executive snapshot:
 *   • KPI tile grid                — top of funnel + revenue + ROI signals
 *   • AI Executive Summary card    — Gemini-generated insights, regen-able
 *   • Charts:
 *       - Monthly Revenue + Leads  (combo line + bar)
 *       - Conversion Funnel        (horizontal bar)
 *       - Performance by Industry  (bar)
 *       - Lead source mix          (pie)
 *   • Top campaigns table          (linked back to each campaign)
 *
 * The whole page consumes one endpoint: GET /api/marketing/growth-dashboard
 * which is computed server-side so we never ship raw lead/deal docs to
 * the browser.
 */
import { useEffect, useState, useCallback } from "react";
import api, { fmtError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkles, RefreshCw, TrendingUp, IndianRupee, Users, Target, FunnelIcon, Activity, Award, Zap } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  BarChart, Bar,
  LineChart, Line,
  PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  ComposedChart,
} from "recharts";

const fmtINR = (n) => {
  const v = Number(n) || 0;
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)} L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(1)} k`;
  return `₹${v.toFixed(0)}`;
};
const fmtNum = (n) => (Number(n) || 0).toLocaleString("en-IN");
const fmtPct = (n) => (n === null || n === undefined ? "—" : `${n}%`);

const KPI_CARDS = [
  { key: "total_campaigns",          label: "Total campaigns",         icon: Activity,    tone: "neutral" },
  { key: "active_campaigns",         label: "Active campaigns",        icon: Zap,         tone: "primary" },
  { key: "marketing_budget",         label: "Marketing budget",        icon: IndianRupee, tone: "neutral", money: true },
  { key: "marketing_spend",          label: "Marketing spend",         icon: IndianRupee, tone: "amber",   money: true },
  { key: "revenue_generated",        label: "Revenue generated",       icon: TrendingUp,  tone: "emerald", money: true },
  { key: "pipeline_generated",       label: "Pipeline value",          icon: TrendingUp,  tone: "sky",     money: true },
  { key: "marketing_roi",            label: "Marketing ROI",           icon: Target,      tone: "emerald", pct: true },
  { key: "cost_per_lead",            label: "Cost per lead",           icon: Users,       tone: "neutral", money: true },
  { key: "customer_acquisition_cost", label: "CAC",                    icon: Users,       tone: "amber",   money: true },
  { key: "marketing_qualified_leads", label: "Marketing-qualified",   icon: Users,       tone: "primary" },
  { key: "sales_qualified_leads",    label: "Sales-qualified",         icon: Users,       tone: "primary" },
  { key: "quotations_generated",     label: "Quotations sent",         icon: Award,       tone: "neutral" },
  { key: "orders_won",               label: "Orders won",              icon: Award,       tone: "emerald" },
  { key: "repeat_business_pct",      label: "Repeat business",         icon: TrendingUp,  tone: "sky",     pct: true },
  { key: "high_value_leads",         label: "High-value leads",        icon: Sparkles,    tone: "amber" },
  { key: "hot_opportunities",        label: "Hot opportunities",       icon: Zap,         tone: "rose" },
];

const TONE_BG = {
  primary:  "bg-primary/10 text-primary border-primary/20",
  emerald:  "bg-emerald-50 text-emerald-700 border-emerald-200",
  sky:      "bg-sky-50 text-sky-700 border-sky-200",
  amber:    "bg-amber-50 text-amber-800 border-amber-200",
  rose:     "bg-rose-50 text-rose-700 border-rose-200",
  neutral:  "bg-muted/50 text-foreground border-border/60",
};

const PIE_COLORS = ["#026928", "#0ea5e9", "#f59e0b", "#e11d48", "#7c3aed", "#10b981", "#ef4444", "#6366f1", "#facc15", "#14b8a6"];

function KpiTile({ icon: Icon, label, value, tone = "neutral", money = false, pct = false, loading = false }) {
  let display;
  if (loading) display = <Skeleton className="h-7 w-20" />;
  else if (value === null || value === undefined) display = <span className="text-muted-foreground text-base">—</span>;
  else if (money) display = fmtINR(value);
  else if (pct)   display = fmtPct(value);
  else            display = fmtNum(value);
  return (
    <Card className="rounded-2xl border-border/60 shadow-soft" data-testid={`growth-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <CardContent className="p-4 flex items-start gap-3">
        <div className={`h-10 w-10 rounded-2xl grid place-items-center border ${TONE_BG[tone]}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground truncate">{label}</p>
          <div className="font-display text-xl font-extrabold mt-0.5">{display}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function AiSummaryCard({ summary, generatedAt, busy, onRegenerate }) {
  return (
    <Card className="rounded-3xl border-primary/20 bg-gradient-to-br from-primary/5 to-emerald-50/40 shadow-soft" data-testid="growth-ai-summary">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-2xl bg-primary text-primary-foreground grid place-items-center">
              <Sparkles className="h-4 w-4" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary">AI Executive Summary</p>
              <p className="text-[11px] text-muted-foreground">{generatedAt ? `Updated ${new Date(generatedAt).toLocaleString()}` : "Awaiting first generation…"}</p>
            </div>
          </div>
          <Button
            onClick={onRegenerate}
            disabled={busy}
            variant="outline"
            size="sm"
            className="rounded-xl tap"
            data-testid="growth-ai-regenerate"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} />
            {busy ? "Generating…" : "Regenerate"}
          </Button>
        </div>
        <p className="text-sm leading-relaxed whitespace-pre-line">
          {summary || "Click Regenerate to produce your first AI summary."}
        </p>
      </CardContent>
    </Card>
  );
}

export default function GrowthDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiBusy, setAiBusy] = useState(false);

  const load = useCallback(async (withAi = true) => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/marketing/growth-dashboard", { params: { ai: withAi } });
      setData(d);
    } catch (err) { toast.error(fmtError(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(true); }, [load]);

  const regenerateAi = async () => {
    setAiBusy(true);
    try {
      const { data: d } = await api.post("/marketing/ai-summary");
      setData((prev) => ({ ...(prev || {}), ai_summary: d.ai_summary, generated_at: d.generated_at }));
      toast.success("AI summary refreshed");
    } catch (err) { toast.error(fmtError(err)); }
    finally { setAiBusy(false); }
  };

  const snapshot = data?.snapshot || {};

  return (
    <div className="space-y-6" data-testid="growth-dashboard">
      {/* AI summary banner */}
      <AiSummaryCard
        summary={data?.ai_summary}
        generatedAt={data?.generated_at}
        busy={aiBusy}
        onRegenerate={regenerateAi}
      />

      {/* KPI grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {KPI_CARDS.map((k) => (
          <KpiTile
            key={k.key}
            icon={k.icon}
            label={k.label}
            value={snapshot[k.key]}
            tone={k.tone}
            money={k.money}
            pct={k.pct}
            loading={loading}
          />
        ))}
      </div>

      {/* Charts row 1: monthly trend + conversion funnel */}
      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="rounded-3xl border-border/60 shadow-soft">
          <CardContent className="p-5">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3">Monthly revenue & leads</p>
            <div className="h-72">
              {loading ? <Skeleton className="h-full w-full rounded-2xl" /> : (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={data?.monthly_trend || []} margin={{ top: 5, right: 16, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 100000 ? `₹${(v/100000).toFixed(0)}L` : `₹${(v/1000).toFixed(0)}k`} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(value, name) => name === "Revenue" ? fmtINR(value) : value}
                      labelStyle={{ fontSize: 12 }}
                      contentStyle={{ borderRadius: 12, fontSize: 12 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar yAxisId="left" dataKey="revenue" name="Revenue" fill="#026928" radius={[8, 8, 0, 0]} barSize={28} />
                    <Line yAxisId="right" type="monotone" dataKey="leads" name="Leads" stroke="#0ea5e9" strokeWidth={2.5} dot={{ r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-3xl border-border/60 shadow-soft">
          <CardContent className="p-5">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3">Conversion funnel</p>
            <div className="h-72">
              {loading ? <Skeleton className="h-full w-full rounded-2xl" /> : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data?.conversion_funnel || []} layout="vertical" margin={{ top: 5, right: 30, bottom: 5, left: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="stage" type="category" tick={{ fontSize: 12, fontWeight: 600 }} width={90} />
                    <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                    <Bar dataKey="count" fill="#026928" radius={[0, 8, 8, 0]} barSize={28} label={{ position: "right", fontSize: 11, fontWeight: 700 }} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts row 2: industry performance + lead source mix */}
      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="rounded-3xl border-border/60 shadow-soft">
          <CardContent className="p-5">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3">Performance by industry</p>
            <div className="h-72">
              {loading ? <Skeleton className="h-full w-full rounded-2xl" /> : (data?.by_industry?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.by_industry} margin={{ top: 5, right: 16, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="industry" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="leads" name="Leads" fill="#0ea5e9" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="converted" name="Converted" fill="#026928" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <EmptyChart text="No industry data yet" />)}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-3xl border-border/60 shadow-soft">
          <CardContent className="p-5">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3">Lead source mix</p>
            <div className="h-72">
              {loading ? <Skeleton className="h-full w-full rounded-2xl" /> : (data?.by_lead_source?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.by_lead_source}
                      dataKey="leads"
                      nameKey="source"
                      cx="50%" cy="50%"
                      outerRadius={92}
                      innerRadius={48}
                      paddingAngle={2}
                      label={(entry) => entry.source.replace(/_/g, " ")}
                    >
                      {data.by_lead_source.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <EmptyChart text="No lead source data yet" />)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top campaigns */}
      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Top campaigns by revenue</p>
            <Badge variant="outline" className="rounded-full text-[10px]">Top {(data?.top_campaigns || []).length}</Badge>
          </div>
          {loading ? <Skeleton className="h-32 w-full rounded-2xl" /> : ((data?.top_campaigns || []).length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b">
                    <th className="py-2 font-bold">Campaign</th>
                    <th className="py-2 font-bold">Type</th>
                    <th className="py-2 font-bold text-right">Budget</th>
                    <th className="py-2 font-bold text-right">Spend</th>
                    <th className="py-2 font-bold text-right">Revenue</th>
                    <th className="py-2 font-bold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_campaigns.map((c) => (
                    <tr key={c.id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="py-2.5 font-semibold">{c.name}</td>
                      <td className="py-2.5 text-xs capitalize text-muted-foreground">{(c.campaign_type || "").replace(/_/g, " ")}</td>
                      <td className="py-2.5 text-right">{fmtINR(c.budget)}</td>
                      <td className="py-2.5 text-right">{fmtINR(c.spend)}</td>
                      <td className="py-2.5 text-right font-bold text-primary">{fmtINR(c.revenue)}</td>
                      <td className="py-2.5"><Badge variant="outline" className="text-[10px] capitalize rounded-full">{c.status || "—"}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <EmptyChart text="No campaigns yet — create your first from the Campaigns tab." />)}
        </CardContent>
      </Card>
    </div>
  );
}

function EmptyChart({ text }) {
  return (
    <div className="h-full grid place-items-center text-muted-foreground text-sm">
      {text}
    </div>
  );
}
